"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SurveyObject = void 0;
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const Uuidable_1 = require("./Uuidable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
/**
 * Base class for survey entities registered in {@link SurveyObjectsRegistry}.
 * Implements {@link IValidatable}: `_isValid` on `_attributes`, plus {@link validate} / {@link isValid}.
 * Extends {@link Uuidable} and holds **completeness** flags (`hasMinimum`, `isStarted`, `isCompleted`)
 * on the subclass `_attributes` object (see {@link CompletableAttributes}).
 *
 * **Subclass contract:** concrete classes must expose a `_attributes` object that includes
 * `CompletableAttributes`; the constructor should list completable keys in the attribute list passed
 * to {@link ConstructorUtils.initializeAttributes} (or equivalent) so values are not treated as custom attributes.
 *
 * **Good candidates to move here later (not done yet):** shared references such as
 * `SurveyObjectsRegistry`, common serialization hooks, or generic `validateParams` fragments—only
 * where every survey entity behaves identically—to avoid pulling composed-only types
 * (e.g. {@link Address}, {@link Routing}) into this hierarchy.
 */
class SurveyObject extends Uuidable_1.Uuidable {
    constructor(_uuid) {
        super(_uuid);
    }
    getValidatableAttributes() {
        return this._attributes;
    }
    get _isValid() {
        return this.getValidatableAttributes()._isValid;
    }
    set _isValid(value) {
        this.getValidatableAttributes()._isValid = value;
    }
    validate() {
        this.getValidatableAttributes()._isValid = true;
        return true;
    }
    isValid() {
        return this._isValid;
    }
    getCompletableAttributes() {
        return this._attributes;
    }
    get hasMinimum() {
        return this.getCompletableAttributes().hasMinimum;
    }
    set hasMinimum(value) {
        this.getCompletableAttributes().hasMinimum = value;
    }
    get isCompleted() {
        return this.getCompletableAttributes().isCompleted;
    }
    set isCompleted(value) {
        this.getCompletableAttributes().isCompleted = value;
    }
    get isStarted() {
        return this.getCompletableAttributes().isStarted;
    }
    set isStarted(value) {
        this.getCompletableAttributes().isStarted = value;
    }
    /**
     * Validates completable boolean fields on a plain params object (for `create` / `validateParams`).
     */
    static validateCompletableParams(dirtyParams, displayName = 'Completable') {
        const errors = [];
        for (const attribute of CompletableAttributes_1.completableAttributeNames) {
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean(attribute, dirtyParams[attribute], displayName));
        }
        return errors;
    }
}
exports.SurveyObject = SurveyObject;
//# sourceMappingURL=SurveyObject.js.map